import { Card, CardHeader } from '../../../components/Card.jsx'
import { Tile, Suppressed, MethodNote, fmtNum, fmtPct } from '../ui.jsx'

/**
 * National Risk Profile — modifiable risk surveillance from each patient's
 * most recent recorded vitals (BP, BMI) plus the latest capillary blood
 * glucose bedside test (Raised BSL: fasting >7 / random >11 mmol/L).
 *
 * Two denominators: BP/BMI bands are of patients with recorded vitals;
 * Raised BSL is of patients with a recorded glucose reading. Each band is
 * k=5 privacy-floor suppressed.
 */
export default function RiskFactors({ data }) {
  const rf = data.risk_factors || {}
  const measuredN = rf.measured_n || 0
  const bslN = rf.bsl_measured_n || 0

  const bands = [
    { key: 'raised_bp', label: 'Raised blood pressure', hint: '>=120/80', colour: 'amber', denom: 'vitals' },
    { key: 'hypertensive', label: 'Hypertensive', hint: '>=140/90', colour: 'rose', denom: 'vitals' },
    { key: 'overweight_or_obese', label: 'Overweight or obese', hint: 'BMI >=25', colour: 'amber', denom: 'vitals' },
    { key: 'obese', label: 'Obese', hint: 'BMI >=30', colour: 'rose', denom: 'vitals' },
    { key: 'raised_bsl', label: 'Raised blood glucose', hint: 'fasting >7 / random >11 mmol/L', colour: 'rose', denom: 'bsl' },
  ]
  const denomNote = (b) => (b.denom === 'bsl' ? 'of glucose readings' : 'of measured')

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="National risk profile"
          subtitle="Measured cardiometabolic risk across all patients with recorded vitals or a capillary glucose reading. Each patient counts once, on their latest reading."
          right={
            <span className="text-[10px] uppercase tracking-wide border rounded-full px-2 py-0.5 bg-amber-50 text-amber-700 border-amber-200">
              measured
            </span>
          }
        />
        <MethodNote>
          BP and BMI bands derive from each patient's most recent recorded
          vitals; Raised blood glucose uses the latest capillary glucose
          bedside test (fasting over 7 or random over 11 mmol/L). Bands below
          the k=5 privacy floor are withheld.
        </MethodNote>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Tile label="Patients measured (vitals)" value={fmtNum(measuredN)} colour="wantok" />
          <Tile label="Glucose readings" value={fmtNum(bslN)} colour="sky" />
          {bands.map((b) => {
            const v = rf[b.key] || {}
            return (
              <Tile
                key={b.key}
                label={b.label}
                value={v.suppressed || v.count == null ? '—' : fmtNum(v.count)}
                sublabel={
                  v.suppressed || v.pct_of_measured == null
                    ? b.hint
                    : `${fmtPct(v.pct_of_measured)} ${denomNote(b)} · ${b.hint}`
                }
                colour={b.colour}
              />
            )
          })}
        </div>
      </Card>

      <Card>
        <CardHeader
          title="Risk band detail"
          subtitle="Share of the relevant measured population in each band. Bands below the k=5 privacy floor are withheld."
        />
        <ul className="space-y-2.5">
          {bands.map((b) => {
            const v = rf[b.key] || {}
            if (v.suppressed || v.count == null) {
              return (
                <li key={b.key} className="text-sm flex items-center justify-between gap-2">
                  <span className="text-slate-500">{b.label} <span className="text-slate-400">({b.hint})</span></span>
                  <Suppressed />
                </li>
              )
            }
            const pct = v.pct_of_measured || 0
            return (
              <li key={b.key} className="text-sm">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-slate-700">
                    {b.label} <span className="text-slate-400">({b.hint})</span>
                  </span>
                  <span className="font-semibold tabular-nums text-slate-900">
                    {fmtNum(v.count)}
                    <span className="text-[11px] text-slate-500 ml-2">{fmtPct(pct)} {denomNote(b)}</span>
                  </span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden mt-1">
                  <div
                    className={`h-full rounded-full ${b.colour === 'rose' ? 'bg-rose-500' : 'bg-amber-500'}`}
                    style={{ width: `${Math.min(100, pct)}%` }}
                  />
                </div>
              </li>
            )
          })}
        </ul>
        {rf.note && (
          <div className="mt-4 text-[11px] text-slate-500 leading-relaxed border-t border-sand-200 pt-2">
            {rf.note}
          </div>
        )}
      </Card>
    </div>
  )
}
